import type { BaseClubDto, ClubFormData, DetailClubDto } from '@/core/data/club/club.interface';
import type { GeoCoords } from '@/core/data/location/location.interface';
import { gambits } from '@/core/gambits';
import { ClubEndpoints } from '@/core/network/api/constants/api.url.constant';
import { baseClubFields, detailClubFields } from '@/core/network/api/constants/club.api_fields';
import { HttpClient } from '@/core/network/http/httpClient';
import type { GamResponse, GetClubTableFilters, GetTableParams } from '@/core/network/http/httpClient.interface';
import type { ClubFilterOptions } from '@/views/composables/constants/main/club.constants';
import { GamFilterSortClub } from '@/views/composables/constants/main/filter.constants';
import type { AxiosResponse } from 'axios';

export class ClubApi {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  async getClubs(
    geo?: GeoCoords,
    filters?: ClubFilterOptions,
    afterCursor?: string | null,
  ): Promise<AxiosResponse<GamResponse<BaseClubDto[]>>> {
    const geoParams = geo
      ? {
          latitude: geo.lat,
          longitude: geo.lng,
          sortBy: GamFilterSortClub.DISTANCE,
          distanceLte: gambits.configService.getGisRadius().default,
        }
      : {};
    return this.httpClient.get<BaseClubDto[], GetClubTableFilters>(ClubEndpoints.GET_CLUBS, {
      config: {
        params: {
          ...geoParams,
          myClubs: !!filters?.myClubs,
          following: !!filters?.following,
          afterCursor,
          fields: baseClubFields.join(','),
        },
      },
    });
  }

  async followClub(id: string): Promise<AxiosResponse<GamResponse<any>>> {
    const result = await this.httpClient.post(ClubEndpoints.CLUB_FOLLOW, {
      urlParams: { id },
    });

    return result;
  }

  async unfollowClub(id: string): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.delete(ClubEndpoints.CLUB_FOLLOW, {
      urlParams: { id },
    });
  }

  async getClub(id: string): Promise<AxiosResponse<GamResponse<DetailClubDto>>> {
    return this.httpClient.get<DetailClubDto, GetTableParams>(ClubEndpoints.GET_CLUB_DETAILS, {
      config: {
        params: {
          fields: detailClubFields.join(','),
        },
      },
      urlParams: { id },
    });
  }

  async getClubByUsername(id: string): Promise<AxiosResponse<GamResponse<DetailClubDto>>> {
    return this.httpClient.get<DetailClubDto, GetTableParams>(ClubEndpoints.GET_CLUB_DETAILS_BY_USERNAME, {
      config: {
        params: {
          fields: detailClubFields.join(','),
        },
      },
      urlParams: { id },
    });
  }

  async addClub(data: ClubFormData): Promise<AxiosResponse<GamResponse<BaseClubDto>>> {
    return await this.httpClient.post(ClubEndpoints.GET_CLUBS, {
      data,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  }

  async updateClub(id: string, data: ClubFormData): Promise<AxiosResponse<GamResponse<BaseClubDto>>> {
    return await this.httpClient.put(ClubEndpoints.GET_CLUB_DETAILS, {
      urlParams: { id },
      data,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  }

  async deleteClub(id: string): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.delete(ClubEndpoints.GET_CLUB_DETAILS, {
      urlParams: { id },
    });
  }

  async addLocation(id: string, locationId: string): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.post(ClubEndpoints.CLUB_LOCATIONS, {
      urlParams: { id },
      data: { locationId },
    });
  }

  async removeLocation(id: string, locationId: string): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.delete(ClubEndpoints.CLUB_LOCATION, {
      urlParams: { id, locationId },
    });
  }

  async setHeadquartersLocation(id: string, locationId: string): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.put(ClubEndpoints.CLUB_LOCATION_MAIN, {
      urlParams: { id },
      data: { locationId },
    });
  }

  async addOrganizer(id: string, username: string): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.post(ClubEndpoints.CLUB_ORGANIZERS, {
      urlParams: { id },
      data: { username },
    });
  }

  async removeOrganizer(id: string, username: string): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.delete(ClubEndpoints.CLUB_ORGANIZER, {
      urlParams: { id, username },
    });
  }

  async changeOwner(id: string, username: string): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.put(ClubEndpoints.CLUB_OWNER, {
      urlParams: { id },
      data: { username },
    });
  }
}
